import { NgTemplateOutlet } from '@angular/common';
import type { TemplateRef } from '@angular/core';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    computed,
    forwardRef,
    inject,
    input,
    model,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, type ControlValueAccessor } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { NgIcon } from '@ng-icons/core';
import { APP_CONFIG_TOKEN } from '@quipex/core/config';
import type { ContextFamily } from '@quipex/shared/utils';
import {
    Dnd,
    QpxButton,
    QpxDropSurface,
    QpxIcon,
    type DropAppearance,
} from '../../directives';
import type { ButtonSize } from '../../directives/button/button';
import { QpxHeroIcon } from '../hero-icon';
import { Loader } from '../loader';
import { Feedback } from './feedback';
import { injectFileIntake, type FileIntakeSource } from './file-intake';
import {
    DEFAULT_ARIA_OPTIONS,
    type AriaOptions,
    type DuplicateDialogContext,
    type DuplicateDialogOptions,
    type FileInputConfig,
    type FilePickerTemplateContext,
    type IconOption,
    type IconOptions,
    type LabelOptions,
    type UploadOptions,
} from './models';
import { Selection } from './selection';

@Component({
    selector: 'qpx-file-upload',
    imports: [
        NgTemplateOutlet,
        MatButtonModule,
        NgIcon,
        Dnd,
        QpxDropSurface,
        Loader,
        QpxHeroIcon,
        QpxButton,
        QpxIcon,
    ],
    templateUrl: './file-upload.html',
    styleUrl: './file-upload.scss',
    providers: [
        Selection,
        Feedback,
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FileUpload),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'file-upload block',
    },
})
export class FileUpload implements ControlValueAccessor {
    //#region Dependency injection
    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    private readonly selection = inject(Selection);
    private readonly intake = injectFileIntake();
    private readonly feedback = inject(Feedback);
    //#endregion

    //#region Constants
    private readonly defaultUploadOptions: UploadOptions = {
        multiple: true,
        withDragAndDrop: true,
        allowFolderSelection: false,
        acceptedFileTypes: this.appConfig.documentUploadTypes.map(
            (ext) => `.${ext}`
        ),
    };
    //#endregion

    //#region Inputs
    public readonly validationOptions = input<boolean | FileInputConfig>(true);

    public readonly uploadOptions = input<
        UploadOptions,
        Partial<UploadOptions>
    >(this.defaultUploadOptions, {
        transform: (value) => ({
            ...this.defaultUploadOptions,
            ...value,
        }),
    });

    public readonly validDropContext = input<ContextFamily>('primary');

    public readonly buttonSize = input<ButtonSize>('sm');

    public readonly icon = input<
        Partial<IconOption> | Partial<IconOptions> | string
    >();

    public readonly duplicateDialogOptions = input<DuplicateDialogOptions>();

    public readonly duplicateDialogTpl =
        input<TemplateRef<DuplicateDialogContext>>();

    public readonly duplicateDialogTplContext =
        input<Record<string, unknown>>();

    public readonly label = input<Partial<LabelOptions> | string>();

    public readonly ariaOptions = input<AriaOptions, Partial<AriaOptions>>(
        DEFAULT_ARIA_OPTIONS,
        {
            transform: (value) => ({
                ...DEFAULT_ARIA_OPTIONS,
                ...value,
            }),
        }
    );

    public readonly filePickerTpl =
        input<TemplateRef<FilePickerTemplateContext> | null>(null);

    public readonly folderPickerTpl =
        input<TemplateRef<FilePickerTemplateContext> | null>(null);

    public readonly comparisonFiles = input<readonly File[]>([]);
    //#endregion

    //#region State
    public readonly isProcessing = model(false);

    private readonly formDisabled = signal(false);
    private readonly addedFiles = signal<File[]>([]);
    private readonly dropTarget = viewChild(Dnd);
    private readonly intakeIssue = signal<string | null>(null);
    protected readonly dropIssue = computed(() =>
        this.dropTarget()?.fileOver() ? null : this.intakeIssue()
    );
    protected readonly dropAppearance = computed<DropAppearance>(() => {
        if (this.formDisabled()) return { state: 'disabled' };
        if (this.isProcessing()) return { state: 'busy' };
        if (this.dropTarget()?.fileOver())
            return { state: 'valid', context: this.validDropContext() };
        return { state: this.dropIssue() ? 'invalid' : 'idle' };
    });
    protected readonly dropLabel = computed(() => {
        switch (this.dropAppearance().state) {
            case 'busy':
                return 'Preparing files…';
            case 'disabled':
                return 'Uploads unavailable';
            case 'invalid':
                return 'These files cannot be added';
            case 'valid':
                return 'Release to add files';
            default:
                return this.dndLabel();
        }
    });
    protected readonly dropContext = computed<ContextFamily>(() => {
        const state = this.dropAppearance().state;
        return state === 'invalid'
            ? 'destructive'
            : state === 'valid'
              ? this.validDropContext()
              : 'secondary';
    });
    protected readonly dropIcon = computed(() => {
        switch (this.dropAppearance().state) {
            case 'disabled':
                return 'lucideLock';
            case 'invalid':
                return 'lucideCircleX';
            case 'valid':
                return 'lucideCircleCheck';
            default:
                return 'lucideUpload';
        }
    });

    protected readonly isDisabled = computed(
        () => this.formDisabled() || this.isProcessing()
    );
    protected readonly multiple = computed(() => this.uploadOptions().multiple);
    protected readonly withDragAndDrop = computed(
        () => this.uploadOptions().withDragAndDrop
    );
    protected readonly allowFolderSelection = computed(
        () => this.uploadOptions().allowFolderSelection
    );
    protected readonly acceptedFileTypes = computed(() => {
        const types = this.uploadOptions().acceptedFileTypes;
        return typeof types === 'string' ? types : types.join(', ');
    });

    protected readonly fileLabel = computed(() => {
        const options = this.label();
        if (typeof options === 'string') return options;
        if (options?.file) return options.file;
        if (this.withDragAndDrop()) return 'Browse files';
        return `Upload file${this.multiple() ? 's' : ''}`;
    });

    protected readonly folderLabel = computed(() => {
        const options = this.label();
        if (
            options != null &&
            typeof options === 'object' &&
            'folder' in options &&
            typeof options.folder === 'string'
        ) {
            return options.folder;
        }

        if (this.withDragAndDrop()) return 'Browse folders';
        return `Upload folder${this.multiple() ? 's' : ''}`;
    });

    protected readonly dndLabel = computed(() => {
        const options = this.label();
        if (
            options != null &&
            typeof options === 'object' &&
            'dnd' in options &&
            typeof options.dnd === 'string'
        ) {
            return options.dnd;
        }

        return 'Drag and drop your documents / files here, or';
    });

    protected readonly fileIcon = computed(() => {
        const options = this.icon();
        const defaultIcon: IconOption = {
            icon: 'lucideUpload',
            set: 'sym-outlined',
            type: 'outlined',
            size: 'sm',
        };

        if (typeof options === 'string') {
            return {
                ...defaultIcon,
                icon: options,
            };
        }

        if (
            options != null &&
            typeof options === 'object' &&
            !('file' in options) &&
            !('folder' in options)
        ) {
            return { ...defaultIcon, ...options };
        }

        if (
            options != null &&
            typeof options === 'object' &&
            'file' in options
        ) {
            if (typeof options.file === 'string') {
                return { ...defaultIcon, icon: options.file };
            }

            if (typeof options.file === 'object') {
                return { ...defaultIcon, ...options.file };
            }
        }

        return defaultIcon;
    });

    protected readonly folderIcon = computed(() => {
        const options = this.icon();
        const defaultIcon: IconOption = {
            icon: 'lucideFolderOpen',
            set: 'sym-outlined',
            type: 'outlined',
            size: 'sm',
        };

        if (
            options != null &&
            typeof options === 'object' &&
            'folder' in options
        ) {
            if (typeof options.folder === 'string') {
                return { ...defaultIcon, icon: options.folder };
            }

            if (typeof options.folder === 'object') {
                return { ...defaultIcon, ...options.folder };
            }
        }

        return defaultIcon;
    });

    protected readonly ids = computed(() => {
        const idPrefix = this.ariaOptions().idPrefix;
        return {
            fileInput: `${idPrefix}-file-input`,
            folderInput: `${idPrefix}-folder-input`,
        };
    });

    protected readonly ariaLabels = computed(() => {
        const ariaLabelSuffix = this.ariaOptions().ariaLabelSuffix;
        return {
            file: `File input for ${ariaLabelSuffix}`,
            folder: `Folder input for ${ariaLabelSuffix}`,
        };
    });

    protected readonly customFilePickerContext =
        computed<FilePickerTemplateContext>(() => ({
            disabled: this.isDisabled(),
            browse: this.openFilePicker,
            id: this.ids().fileInput,
        }));

    protected readonly customFolderPickerContext =
        computed<FilePickerTemplateContext>(() => ({
            disabled: this.isDisabled(),
            browse: this.openFolderPicker,
            id: this.ids().folderInput,
        }));
    //#endregion

    //#region Outputs
    public readonly fileListChange = output<File[]>();
    //#endregion

    //#region Template references
    public readonly fileInput = viewChild<
        unknown,
        ElementRef<HTMLInputElement>
    >('fileInput', { read: ElementRef });
    public readonly folderInput = viewChild<
        unknown,
        ElementRef<HTMLInputElement>
    >('folderInput', { read: ElementRef });
    //#endregion

    //#region ControlValueAccessor state
    private isFormBound = false;
    private onChange?: (value: File[]) => void;
    private onTouched?: () => void;
    //#endregion

    //#region Public methods
    public writeValue(value: File[] | null): void {
        this.intakeIssue.set(null);
        this.addedFiles.set(Array.isArray(value) ? value : []);
        this.resetNativeInputs();
    }

    public registerOnChange(fn: (value: File[]) => void): void {
        this.onChange = fn;
        this.isFormBound = true;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.formDisabled.set(isDisabled);
    }

    public clear(): void {
        this.intakeIssue.set(null);
        this.addedFiles.set([]);

        if (!this.isFormBound) {
            this.fileListChange.emit([]);
        }

        this.onChange?.([]);
        this.onTouched?.();
        this.resetNativeInputs();
    }
    //#endregion

    //#region Template methods
    protected readonly openFilePicker = () =>
        this.fileInput()?.nativeElement.click();

    protected readonly openFolderPicker = () =>
        this.folderInput()?.nativeElement.click();

    protected async onDrop(event: DragEvent): Promise<void> {
        if (this.isDisabled() || !event.dataTransfer) return;

        this.intakeIssue.set(null);
        this.isProcessing.set(true);
        await this.handleIncomingFiles({
            kind: 'drop',
            dataTransfer: event.dataTransfer,
        });
    }

    protected async onBrowse(event: Event): Promise<void> {
        if (!(event.target instanceof HTMLInputElement)) return;

        const files = this.selection.readFilesFromBrowse(
            event.target,
            this.allowFolderSelection()
        );
        if (!files.length) return;

        this.intakeIssue.set(null);
        this.isProcessing.set(true);
        await this.handleIncomingFiles({ kind: 'files', files });
    }
    //#endregion

    //#region Private methods
    private async handleIncomingFiles(source: FileIntakeSource): Promise<void> {
        try {
            const config = this.validationOptions();
            const result = await this.intake.prepare({
                source,
                comparisonFiles: this.comparisonFiles(),
                validation:
                    typeof config === 'object'
                        ? config
                        : config === false
                          ? false
                          : {},
            });

            if (result.duplicateNames.length > 0) {
                await this.feedback.showDuplicateDialog({
                    duplicateNames: result.duplicateNames,
                    dialogOptions: this.duplicateDialogOptions(),
                    dialogTemplate: this.duplicateDialogTpl(),
                    dialogTemplateContext: this.duplicateDialogTplContext(),
                });
            }

            if (
                result.issues.length > 0 &&
                (typeof config !== 'object' || config.errorHandler !== 'custom')
            ) {
                this.feedback.showValidationIssues(result.issues);
            }

            if (result.kind === 'rejected') {
                this.intakeIssue.set(
                    result.reason === 'aborted' ? null : result.message
                );
                this.abortProcessing();
                return;
            }

            this.commit(result.files);
        } catch {
            this.abortProcessing();
        }
    }

    private commit(files: File[]): void {
        this.addedFiles.set(files);
        this.isProcessing.set(false);

        if (!this.isFormBound) {
            this.fileListChange.emit(files);
        }

        this.onChange?.(files);
        this.onTouched?.();
        this.resetNativeInputs();
    }

    private abortProcessing(): void {
        this.isProcessing.set(false);
        this.onTouched?.();
        this.resetNativeInputs();
    }

    private resetNativeInputs(): void {
        const fileElement = this.fileInput()?.nativeElement;
        if (fileElement) fileElement.value = '';

        const folderElement = this.folderInput()?.nativeElement;
        if (folderElement) folderElement.value = '';
    }
    //#endregion
}
